import React, { memo, useCallback, useMemo } from "react";

import { useFormikContext } from "formik";
import { compareValues } from "objs/_ConfiguratorObjs/Wizard/SubComponents/WizardQuestionnaire/questionnaireHelpers";
import { ContentByField } from "./ContentByField";

const toggleAffectedBy = (affectedBy, fieldKey) => {
  if (!affectedBy) {
    return [fieldKey];
  }

  if (affectedBy.includes(fieldKey)) {
    return affectedBy.filter((k) => k !== fieldKey);
  }

  return [...affectedBy, fieldKey];
};

export const QuestionnaireField = memo(({ field, ...restProps }) => {
  const { setFieldValue, values } = useFormikContext();

  const onChange = useCallback(
    (e) => {
      const { checked, name, value } = e.target;
      if (field.onAction) {
        const { affectedBy: currentFieldAffectedBy, onAction: baseOnAction } =
          values.fieldsRelation[field.fieldKey];

        const fieldOnAction =
          baseOnAction.fieldValueChange.length === 1
            ? baseOnAction.fieldValueChange[0]
            : baseOnAction.fieldValueChange.filter((state) => {
                if (state.newValue === checked) {
                  return true;
                }

                if (typeof state.newValue === "string") {
                  const val = state.newValue.slice(1);
                  return Number(val) === value;
                }

                return false;
              })[0];

        const statesToModify = fieldOnAction.states;
        const fieldRelationUpdates = {};
        if (field.fieldInputType === "input" && field.fieldType === "int") {
          if (compareValues(value, fieldOnAction.newValue)) {
            statesToModify.forEach(
              ({ fieldKey: fieldKeyRule, fieldValue: fieldValueRule }) => {
                const { name: fieldName } = values.fieldsRelation[fieldKeyRule];
                if (values[fieldName] !== fieldValueRule) {
                  setFieldValue(fieldName, fieldValueRule);
                }
              },
            );
          }
          setFieldValue(name, value);
        }
        if (
          field.fieldInputType === "radiobutton" ||
          fieldOnAction.newValue === checked ||
          statesToModify.some((item) => item.fieldInputType === "radiobutton")
        ) {
          statesToModify.forEach(
            ({
              fieldKey: fieldKeyRule,
              fieldValue: fieldValueRule,
              enabled: enabledRule,
              fieldInputType,
            }) => {
              const {
                name: fieldName,
                affectedBy,
                onAction,
                fieldDefaultValue,
              } = values.fieldsRelation[fieldKeyRule];

              if (
                fieldInputType === "radiobutton" &&
                fieldValueRule === false
              ) {
                const updatedAffectedBy = toggleAffectedBy(
                  affectedBy,
                  field.fieldKey,
                );
                fieldRelationUpdates[fieldKeyRule] = {
                  name: fieldName,
                  onAction,
                  fieldDefaultValue,
                  enabled:
                    updatedAffectedBy.length === 0 ? undefined : enabledRule,
                  affectedBy: updatedAffectedBy,
                };
                return;
              }
              if (fieldInputType === "radiobutton" && fieldValueRule === true) {
                const splitFieldKey = fieldKeyRule.split(".");
                setFieldValue(
                  `${splitFieldKey[0]}/#radiobutton`,
                  `${splitFieldKey[0]}/#${splitFieldKey[0]},${splitFieldKey[1]}/#bool`,
                );
                return;
              }

              // case when onAction doesn't give permission to enable/disable field
              if (
                enabledRule === undefined &&
                currentFieldAffectedBy?.length === 1 &&
                affectedBy.length === 1 &&
                JSON.stringify(currentFieldAffectedBy) ===
                  JSON.stringify(affectedBy) &&
                fieldValueRule !== values[fieldName]
              ) {
                setFieldValue(fieldName, !values[fieldName]);
                // check how many fields affected on this field and fields that contains inside it's onAction
              }

              // case when onAction give permission to enable/disable field
              if (enabledRule && fieldValueRule !== undefined) {
                setFieldValue(fieldName, fieldValueRule);

                // field.fieldKey - parent filed on which onAction applied
                fieldRelationUpdates[fieldKeyRule] = {
                  name: fieldName,
                  onAction,
                  fieldDefaultValue,
                  enabled: enabledRule,
                  affectedBy: affectedBy
                    ? [...affectedBy, field.fieldKey]
                    : [field.fieldKey],
                };
              }
            },
          );
        } else {
          statesToModify.forEach(({ fieldKey: fieldKeyRule }) => {
            const {
              name: fieldName,
              enabled,
              affectedBy,
              onAction,
              fieldDefaultValue,
            } = values.fieldsRelation[fieldKeyRule];

            // case when only current parent field allows onAction child be active
            if (affectedBy?.length === 1 && affectedBy[0] === field.fieldKey) {
              fieldRelationUpdates[fieldKeyRule] = {
                name: fieldName,
                enabled: false,
                affectedBy: [],
                onAction,
                fieldDefaultValue,
              };
              setFieldValue(fieldName, fieldDefaultValue);
            }

            // case when multiple fields allows onAction child to be active
            if (affectedBy?.length > 1) {
              // getting parent that still affect this field
              const stillAffectedBy = affectedBy.filter(
                (item) => item !== field.fieldKey,
              );

              fieldRelationUpdates[fieldKeyRule] = {
                name: fieldName,
                enabled,
                affectedBy: stillAffectedBy,
                onAction,
                fieldDefaultValue,
              };

              // if child checked and has own onAction that depends on parent rules we should check is it should leave as checked or not
              if (onAction && values[fieldName]) {
                let finalValue = false;
                stillAffectedBy.forEach((relatedParent) => {
                  if (!finalValue) {
                    finalValue = values.fieldsRelation[
                      relatedParent
                    ].onAction.fieldValueChange[0].states.find(
                      (item) => item.fieldKey === fieldKeyRule,
                    ).fieldValue;
                  }
                });

                const fieldsOnActionStates =
                  onAction.fieldValueChange[0].states;
                fieldsOnActionStates.forEach((relatedField) => {
                  if (!finalValue) {
                    finalValue =
                      relatedField.fieldValue ===
                      values[
                        values.fieldsRelation[relatedField.fieldKey].name
                      ].toString();
                  }
                });

                setFieldValue(fieldName, finalValue);
              }
            }
          });
        }

        if (field.fieldInputType === "radiobutton") {
          setFieldValue(name, value);
        }

        if (field.fieldInputType === "checkbox") {
          setFieldValue(name, checked);
        }

        setFieldValue("fieldsRelation", {
          ...values.fieldsRelation,
          ...fieldRelationUpdates,
        });
      }
      if (values.resetFieldValue) {
        setFieldValue("resetFieldValue", undefined);
      }
    },

    [values, field, setFieldValue],
  );

  const isDisabled = useMemo(() => {
    if (values?.resetFieldValue) {
      return !field.enabled;
    }

    if (values?.fieldsRelation[field.fieldKey]?.enabled === false) {
      return true;
    }

    if (values?.fieldsRelation[field.fieldKey]?.enabled) {
      return false;
    }

    return !field.enabled;
  }, [
    field.enabled,
    field.fieldKey,
    values?.fieldsRelation,
    values?.resetFieldValue,
  ]);

  return (
    <ContentByField
      field={field}
      onChange={field.onAction ? onChange : null}
      disabled={isDisabled}
      {...restProps}
    />
  );
});
